import React, { useEffect, useState } from "react";
import * as stylex from "@stylexjs/stylex";
import { Input, Textarea, Message, Toggle } from "@zendeskgarden/react-forms";
import { Option, Combobox, Field, type IComboboxProps } from "@zendeskgarden/react-dropdowns";
import { Formik, Field as FormikField, Form, type FormikHelpers, type FormikProps, type FieldProps } from "formik";
import * as Yup from "yup";
import { useMutation, useQuery } from "urql";
import { Button } from "@zendeskgarden/react-buttons";
import { Alert } from "@zendeskgarden/react-notifications";
import uniqBy from "lodash/uniqBy";
import { LabelGroupType } from "@linear/common/models/LabelGroupType";
import { SortHelper } from "@linear/common/utils/SortHelper";
import { CreateAttachmentMutation, CreateIssueMutation, type IActiveTeamQuery, type ICreateAttachmentMutation, type ICreateIssueMutation, type ITeamQuery, type ITeamSearchQuery, type IViewerQuery, ActiveTeamQuery, TeamQuery, TeamSearchQuery } from "../queries";
import type { Label, Team, TemplateData, User, ZendeskTicket } from "../types";
import { Constants } from "../constants";
import { attachmentForZendeskConversation } from "../utils/formattingUtils";
import { userFacingErrorMessage } from "../utils/errorUtils";
import "../styles/variables.stylex";
import { ZendeskThemeVariables } from "../styles/variables.stylex";
const NewIssueSchema = Yup.object().shape({
  title: Yup.string().required("Required"),
  team: Yup.string().required("Required"),
  priority: Yup.number(),
  assignee: Yup.string(),
  label: Yup.string()
});
type Props = {
  title?: string;
  description?: string;
  templateData?: TemplateData;
  ticket: ZendeskTicket;
  teams: Team[];
  priorities: IViewerQuery["issuePriorityValues"];
  onCreate(): void;
  onCancel(): void;
};
type Priority = IViewerQuery["issuePriorityValues"][0];
const INITIAL_TEAM_PAGE_SIZE = 50;
const TEAM_SEARCH_DEBOUNCE_MS = 200;
interface FormValues {
  title: string;
  description: string;
  includeMessage: boolean;
  team: string;
  priority?: number;
  assignee?: string;
  labels: string[];
}
interface SubmitError {
  title: string;
  message: string;
}

/**
 * Guides an agent through creating a new Linear issue from the current Zendesk ticket and linking it immediately.
 *
 * Use this when the ticket does not already map to existing work and the agent needs to choose team, labels, assignee,
 * and optional template defaults. If linking fails after creation, submitting again retries the link using the created
 * issue rather than creating another issue.
 *
 * @param props The component properties described by {@link Props} .
 * @returns The rendered content.
 */
export function CreateForm(props: Props) {
  const {
    ticket,
    teams,
    priorities,
    templateData,
    onCreate,
    onCancel
  } = props;
  const {
    id: templateId
  } = templateData || {};
  const textareaRef = React.createRef<HTMLTextAreaElement>();
  const [initialTeamId] = React.useState(() => {
    return templateData?.teamId || localStorage.getItem(Constants.teamCacheKey) || teams[0]?.id;
  });
  const [includeMessage] = React.useState<boolean>(localStorage.getItem(Constants.includeMessagePreferenceKey) === "true");
  const [createIssueResult, createIssue] = useMutation<ICreateIssueMutation>(CreateIssueMutation);
  const [createAttachmentResult, createAttachment] = useMutation<ICreateAttachmentMutation>(CreateAttachmentMutation);
  const fetching = createIssueResult.fetching || createAttachmentResult.fetching;
  const [submitError, setSubmitError] = useState<SubmitError | undefined>(undefined);
  const submitErrorRef = React.useRef<HTMLDivElement | null>(null);
  // Remembers an issue created by a submit whose ticket linking failed, so that resubmitting retries the linking
  // instead of creating a duplicate issue.
  const createdIssueIdRef = React.useRef<string | undefined>(undefined);

  // The alert sits below the sticky footer at the end of the scrollable form, which can be below the fold.
  useEffect(() => {
    if (submitError) {
      submitErrorRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest"
      });
    }
  }, [submitError]);
  const quotedMessage = React.useMemo(() => ticket.firstMessage?.body.trim().split("\n").map(row => `> ${row}`).join("\n") ?? "", [ticket]);
  const baseDescription = props.description || templateData?.description || "";
  const initialDescription = includeMessage && quotedMessage ? `${quotedMessage}\n\n${baseDescription}` : baseDescription;
  const handleSubmit = async (values: FormValues, {
    setSubmitting
  }: FormikHelpers<FormValues>) => {
    if (fetching) {
      setSubmitting(false);
      return;
    }
    setSubmitError(undefined);
    localStorage.setItem(Constants.teamCacheKey, values.team);
    let issueId = createdIssueIdRef.current;
    if (!issueId) {
      const issueResult = await createIssue({
        teamId: values.team,
        title: values.title,
        description: values.description,
        assigneeId: values.assignee,
        labelIds: values.labels || [],
        priority: values.priority,
        templateId
      });
      const issue = issueResult.data?.issueCreate?.issue;
      if (!issue) {
        setSubmitError({
          title: "Failed to create issue",
          message: userFacingErrorMessage(issueResult.error, "Something went wrong while creating the issue. Please try again.")
        });
        setSubmitting(false);
        return;
      }
      issueId = issue.id;
      createdIssueIdRef.current = issueId;
    }
    const attachmentResult = await createAttachment({
      ...attachmentForZendeskConversation(ticket),
      issueId
    });
    if (attachmentResult.error) {
      setSubmitError({
        title: "Issue created, but linking failed",
        message: userFacingErrorMessage(attachmentResult.error, "The issue was created but could not be linked to this ticket. Submit again to retry linking.")
      });
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
    onCreate();
  };
  function handleIncludeMessage(form: FormikProps<FormValues>, checked: boolean) {
    if (checked) {
      void form.setFieldValue("description", `${quotedMessage}\n\n${form.values.description}`);
    } else if (form.values.description.startsWith(quotedMessage)) {
      void form.setFieldValue("description", form.values.description.replace(quotedMessage, "").trim());
    }
    localStorage.setItem(Constants.includeMessagePreferenceKey, checked.toString());
  }
  const handleKeyboardSubmit = (event: React.KeyboardEvent, submit: () => void) => {
    if (event.metaKey && event.key === "Enter") {
      submit();
    }
  };
  return <div className="sx-exx8yu sx-mzs88n sx-18d9i69 sx-6wrskw">
      <Formik initialValues={{
      title: props.title || templateData?.title || ticket.subject || "",
      includeMessage,
      description: initialDescription,
      team: initialTeamId,
      labels: templateData?.labelIds || [],
      assignee: templateData?.assigneeId,
      priority: templateData?.priority
    }} validationSchema={NewIssueSchema} onSubmit={handleSubmit}>
        {(form: FormikProps<FormValues>) => <Form onKeyDown={e => handleKeyboardSubmit(e, form.submitForm)}>
            <Field className="sx-1c436fg">
              <Field.Label>Title</Field.Label>
              <FormikField name="title">
                {({
              field
            }: FieldProps<string, FormValues>) => <>
                    <Input isCompact autoFocus {...field} />

                    {form.submitCount > 0 && form.touched.title && form.errors.title && <Message validation="error">{form.errors.title}</Message>}
                  </>}
              </FormikField>
            </Field>
            <Field className="sx-1c436fg">
              <Field.Label>Description</Field.Label>
              <FormikField name="description">
                {({
              field
            }: FieldProps<string, FormValues>) => <Textarea minRows={4} maxRows={10} isCompact {...field} />}
              </FormikField>
            </Field>
            <Field className="sx-1c436fg">
              <FormikField name="includeMessage" type="checkbox">
                {({
              field
            }: FieldProps<string, FormValues>) => <Toggle id="includeMessage" {...field} onChange={e => {
              handleIncludeMessage(form, e.target.checked);
              field.onChange(e);
              textareaRef.current?.focus();
            }}>
                    <Field.Label htmlFor="includeMessage" isRegular>
                      Include message
                    </Field.Label>
                  </Toggle>}
              </FormikField>
            </Field>

            <TeamField form={form} initialTeams={teams} />

            <FormikField name="priority">
              {({
            field
          }: FieldProps<number, FormValues>) => {
            const selectedPriority = field.value ? priorities.find(priority => priority.priority === field.value) : undefined;
            return <FilteredCombobox label="Priority" options={priorities} nameForOption={(priority: Priority) => priority.label} onSelect={priority => {
              void form.setFieldValue("priority", Number(priority));
            }} selectionValue={String(selectedPriority?.priority)}>
                    {({
                options,
                hiddenOptions
              }) => options.map((priority: Priority) => <Option key={priority.priority} value={String(priority.priority)} label={priority.label} isSelected={priority.priority === field.value} isHidden={hiddenOptions.has(priority)} />)}
                  </FilteredCombobox>;
          }}
            </FormikField>

            <TeamFormFields form={form} teamId={form.values.team} key={form.values.team} />

            <div className="sx-7wzq59 sx-1ey2m1c sx-z9dl7a sx-n9heto sx-1c436fg">
              <div className="sx-78zum5 sx-tqikln">
                <Button isPrimary type="submit" disabled={form.isSubmitting}>
                  {form.isSubmitting ? "Creating…" : "Create issue"}
                </Button>
                <Button isBasic onClick={onCancel}>
                  Cancel
                </Button>
              </div>
            </div>
            {submitError && <Alert className="sx-1c436fg" type="error" ref={submitErrorRef}>
                <Alert.Title>{submitError.title}</Alert.Title>
                {submitError.message}
              </Alert>}
          </Form>}
      </Formik>
    </div>;
}

// -- Team related form fields

const TeamFormFields = ({
  teamId,
  form
}: {
  form: FormikProps<FormValues>;
  teamId?: string;
}) => {
  const [allLabels, setAllLabels] = useState<Label[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [result] = useQuery<ITeamQuery>({
    query: TeamQuery,
    variables: {
      teamId,
      after: cursor
    },
    pause: teamId === undefined || !hasMore
  });
  const {
    data,
    fetching,
    error
  } = result;
  const users = result.data?.team?.members.nodes || [];
  const labels = result.data?.team?.labels.nodes || [];

  // Effect to handle new data and trigger next page fetch
  useEffect(() => {
    if (!fetching && data && hasMore) {
      const labelsPageInfo = data.team?.labels?.pageInfo;

      // Update our accumulated list
      setAllLabels(prev => uniqBy([...prev, ...labels], "id"));

      // Check if there are more pages
      if (labelsPageInfo?.hasNextPage) {
        // Trigger fetch of next page
        setCursor(labelsPageInfo.endCursor);
      } else {
        // We've reached the end
        setHasMore(false);
        // Update the form to remove labels from previous team
        void form.setFieldValue("labels", form.values.labels.filter(label => [...allLabels, ...labels].find(l => l.id === label)));
      }
    }
  }, [data, fetching, hasMore]);

  // Handle errors
  useEffect(() => {
    if (error) {
      setHasMore(false);
    }
  }, [error]);

  // filter labels to not include groups and sort by name
  const labelGroups = new Set(allLabels.filter(label => labels.find(l => l.parent?.id === label.id)));
  const rootLabels = allLabels.filter(label => !labelGroups.has(label) && !label.retiredAt);
  const labelOptions = SortHelper.natural(rootLabels, label => `${label.parent?.name || ""} ${label.name}`).map(label => ({
    ...label,
    name: label.parent ? `${label.parent.name} → ${label.name}` : label.name
  }));
  if (result.fetching || hasMore) {
    // Render dummy fields to avoid layout shift and a bug where the Combobox options will not re-render when the query
    // returns.
    return <>
        <Field className="sx-1c436fg">
          <Field.Label>Assignee</Field.Label>
          <Combobox isAutocomplete={true} isCompact={true} maxHeight="auto"></Combobox>
        </Field>
        <Field className="sx-1c436fg">
          <Field.Label>Labels</Field.Label>
          <Combobox isAutocomplete={true} isCompact={true} maxHeight="auto"></Combobox>
        </Field>
      </>;
  }
  return <>
      <FormikField name="assignee">
        {({
        field
      }: FieldProps<string, FormValues>) => {
        const assignee = field.value && users ? users.find(user => user.id === field.value) : undefined;
        return <FilteredCombobox label="Assignee" options={SortHelper.natural(users, "name")} nameForOption={(user: User) => user.name} onSelect={userId => {
          void form.setFieldValue("assignee", userId);
        }} selectionValue={assignee?.id ?? ""}>
              {({
            options,
            hiddenOptions
          }) => options.map((user: User) => <Option key={user.id} value={user.id} label={user.name} isSelected={user.id === field.value} isHidden={hiddenOptions.has(user)} />)}
            </FilteredCombobox>;
      }}
      </FormikField>
      <FormikField name="labels">
        {({
        field
      }: FieldProps<string, FormValues>) => {
        const selectedLabels = field.value ? labelOptions.filter(label => field.value.includes(label.id)).map(label => label.id) : [];
        return <FilteredCombobox label="Labels" options={labelOptions} multiSelect={true} nameForOption={(label: Label) => label.name} onSelect={(labelIds: string[]) => {
          void form.setFieldValue("labels", uniqBy(labelIds.reverse(), labelId => {
            const label = labelOptions.find(l => l.id === labelId);
            if (label?.parent?.groupType === LabelGroupType.multiSelect) {
              return labelId;
            }
            return label?.parent?.id || labelId;
          }).reverse());
        }} selectionValue={selectedLabels}>
              {({
            options,
            hiddenOptions
          }) => options.map((label: Label) => <Option key={label.id} value={label.id} label={label.name} isSelected={field.value.includes(label.id)} isHidden={hiddenOptions.has(label)} />)}
            </FilteredCombobox>;
      }}
      </FormikField>
    </>;
};
const TeamField = ({
  form,
  initialTeams
}: {
  form: FormikProps<FormValues>;
  initialTeams: Team[];
}) => {
  const [input, setInput] = React.useState("");
  const [search, setSearch] = React.useState("");
  React.useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearch(input.trim());
    }, TEAM_SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [input]);
  const [result] = useQuery<ITeamSearchQuery>({
    query: TeamSearchQuery,
    variables: {
      first: INITIAL_TEAM_PAGE_SIZE,
      filter: search ? {
        or: [{
          name: {
            containsIgnoreCase: search
          }
        }, {
          key: {
            containsIgnoreCase: search
          }
        }]
      } : undefined
    }
  });
  const searchedTeams = React.useMemo(() => {
    const sourceTeams = result.data?.teams.nodes ?? initialTeams;
    return SortHelper.natural(sourceTeams.filter(team => !team.retiredAt), team => team.displayName);
  }, [initialTeams, result.data?.teams.nodes]);
  const selectedTeamInResults = searchedTeams.find(team => team.id === form.values.team);
  const [selectedTeamResult] = useQuery<IActiveTeamQuery>({
    query: ActiveTeamQuery,
    variables: {
      teamId: form.values.team
    },
    pause: !form.values.team || selectedTeamInResults !== undefined
  });
  React.useEffect(() => {
    if (!form.values.team || selectedTeamResult.fetching || selectedTeamResult.error || initialTeams.length === 0 || selectedTeamResult.data?.teams.nodes.length !== 0) {
      return;
    }
    void form.setFieldValue("team", initialTeams[0]?.id);
  }, [form, form.values.team, initialTeams, selectedTeamResult]);
  const selectedTeam = selectedTeamInResults ?? selectedTeamResult.data?.teams.nodes[0];
  const options = React.useMemo(() => {
    if (!selectedTeam || searchedTeams.some(team => team.id === selectedTeam.id) || selectedTeam.retiredAt) {
      return searchedTeams;
    }
    return SortHelper.natural([...searchedTeams, selectedTeam], team => team.displayName);
  }, [searchedTeams, selectedTeam]);
  const handleChange: IComboboxProps["onChange"] = change => {
    if (change.type === "input:change") {
      setInput(change.inputValue ?? "");
      return;
    }
    if (change.selectionValue) {
      setInput("");
      void form.setFieldValue("team", change.selectionValue);
    }
  };
  return <Field className="sx-1c436fg">
      <Field.Label>Team</Field.Label>
      <Combobox isAutocomplete={true} isCompact={true} defaultActiveIndex={0} maxHeight="auto" selectionValue={selectedTeam?.id} renderValue={({
      inputValue
    }) => selectedTeam?.displayName ?? inputValue} inputValue={input || undefined} onChange={handleChange}>
        {options.map(team => <Option key={team.id} value={team.id} label={team.displayName} isSelected={team.id === form.values.team} />)}
        {!result.fetching && options.length === 0 && <Option isDisabled label="" value="No matches found" />}
      </Combobox>
    </Field>;
};
type FilteredComboboxProps = {
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  children(renderProps: {
    options: any[];
    hiddenOptions: Set<any>;
  }): React.ReactNode;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  options: any[];
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  nameForOption(model: any): string;
  /**
   * The callback to call when an option is selected.
   *
   * @param option The selected value, selected values, or null when cleared.
   */
  onSelect(option: string | string[] | null): void;
  /** The label of the combobox. */
  label: string;
  /** Whether the combobox is multi-select. */
  multiSelect?: boolean;
  /** The selection value of the combobox. */
  selectionValue?: string | string[] | null;
};
const FilteredCombobox = (props: FilteredComboboxProps) => {
  const {
    options,
    nameForOption,
    children,
    label
  } = props;
  const [input, setInput] = React.useState<string>("");
  const [hiddenOptions, setHiddenOptions] = React.useState(new Set());
  React.useEffect(() => {
    if (!input || input.length === 0) {
      setHiddenOptions(new Set());
      return;
    }
    const filteredOptions = options.filter(option => nameForOption(option).toLowerCase().indexOf(input.trim().toLowerCase()) === -1);
    setHiddenOptions(new Set(filteredOptions));
  }, [options, input]);
  const handleChange: IComboboxProps["onChange"] = change => {
    if (change.type === "input:change") {
      setInput(change.inputValue ?? "");
    } else if (change.selectionValue) {
      props.onSelect?.(change.selectionValue);
    }
  };
  return <Field className="sx-1c436fg">
      <Field.Label>{label}</Field.Label>
      <Combobox isAutocomplete={true} isCompact={true} defaultActiveIndex={0} maxHeight="auto" isMultiselectable={!!props.multiSelect} selectionValue={props.selectionValue} inputValue={props.multiSelect ? input : undefined} onChange={handleChange}>
        {children({
        options,
        hiddenOptions
      })}
        {hiddenOptions.size === options.length && <Option isDisabled label="" value="No matches found" />}
      </Combobox>
    </Field>;
};